/** Corpo di POST /api/auth/login (sso-bck: routes/AuthRoute.kt). */
export interface LoginRequest {
  username: string;
  password: string;
  /**
   * Riferimento opaco alla richiesta /sso/v1/authorize in corso, generato da sso-bck
   * e passato a questo FE come query param. Assente se l'utente arriva qui
   * direttamente: in quel caso il login crea solo la sessione centrale.
   */
  opaque: string | null;
}

export interface LoginResponse {
  /**
   * URL di callback dell'applicazione richiedente, con `code` e `state`.
   * `null` quando il login non fa parte di un flusso OIDC (nessun opaque), e quando manca
   * ancora il codice della verifica in due passaggi.
   */
  redirectUri: string | null;
  /**
   * Presente quando la password è giusta ma serve anche il codice TOTP: il login non è
   * concluso finché il codice non arriva su POST /api/auth/login/totp.
   */
  totp?: LoginTotp | null;
}

/**
 * Il secondo passaggio del login (LoginResponse.Totp in sso-bck).
 *
 * - `VERIFY`: l'utente ha già un'app di autenticazione collegata e deve solo dare il codice.
 * - `ENROLL`: la verifica è obbligatoria ma l'utente non ha ancora collegato l'app: prima
 *   la configura con `uri` (QR) o `secret` (inserimento a mano), poi dà il primo codice, che
 *   conferma il collegamento.
 *
 * `challenge` identifica il login in sospeso. Scade (10 minuti di default) e si consuma col
 * primo codice valido: dopo, si riparte dalla password.
 */
export interface LoginTotp {
  step: 'VERIFY' | 'ENROLL';
  challenge: string;
  uri: string | null;
  secret: string | null;
}

/** Corpo di POST /api/auth/login/totp. */
export interface LoginTotpRequest {
  challenge: string;
  code: string;
}

/**
 * Risposta di GET /api/auth/login-detail?opaque=… (sso-bck: routes/AuthRoute.kt).
 *
 * Serve a dire all'utente *verso cosa* sta accedendo. Il servizio legge l'opaque con `peek`
 * e non con `pop`, quindi la chiamata non lo consuma e il login può procedere normalmente.
 */
export interface LoginDetail {
  clientDisplayName: string;
  /** Assente per i client che non hanno configurato un logo dal backoffice. */
  clientLogoUrl: string | null;
  /**
   * Palette con cui vestire la pagina, configurata dal backoffice.
   *
   * Volutamente non tipizzata oltre questo: sso-bck la dichiara `Map<String, Any?>` e non
   * la valida, quindi qui può arrivare qualunque forma. Le chiavi riconosciute e i vincoli
   * sui valori stanno in `core/theme/client-theme.ts`, che è anche l'unico punto da cui
   * questi dati raggiungono il CSS. Vuota — `{}` — per i client non personalizzati.
   */
  clientStyles: Record<string, unknown>;
  /**
   * Tipi di account federati attivi su sso-bck (es. `ENTRA`): per ognuno la pagina offre un
   * pulsante che avvia `/api/auth/federated/{tipo}/start`. Vuoto se nessuno è abilitato.
   */
  providers: string[];
}

/**
 * Provider federati che la pagina sa presentare. Un tipo non elencato qui viene comunque
 * offerto, con un'etichetta generica: meglio un pulsante spoglio che un accesso mancante.
 */
export const FEDERATED_PROVIDER_LABEL: Record<string, string> = {
  ENTRA: 'Accedi con Microsoft',
};

/**
 * Esiti negativi di un login federato (SSOError.LoginRedirect.Code). Ogni esito del flusso
 * federato è un redirect, quindi sso-bck rimanda il browser al login con `?error=<codice>`,
 * in minuscolo, e con l'opaque quando la richiesta è ancora riprendibile.
 */
export type LoginRedirectError =
  | 'invalid_request'
  | 'expired_request'
  | 'invalid_state'
  | 'upstream_denied'
  | 'upstream_error'
  | 'unknown_identity'
  | 'username_taken'
  | 'not_active'
  | 'access_requested'
  | 'server_error';

/** Corpo d'errore di sso-bck (errors/AppError.kt), serializzato per ogni fallimento. */
export interface ApiError {
  message: string;
  errorType: string;
  data?: unknown;
  cause?: ApiError | null;
}

export const ERROR_TYPE = {
  invalidOpaque: 'dev.atlantis.sso.errors.SSOError.InvalidOpaque',
  invalidCredentials: 'dev.atlantis.sso.errors.AccountError.InvalidCredentials',
  notActive: 'dev.atlantis.sso.errors.AccountError.NotActive',
  /** Codice sbagliato: il login in sospeso resta valido, si può riprovare. */
  invalidTotpCode: 'dev.atlantis.sso.errors.TotpError.InvalidCode',
  /** Login in sospeso scaduto o già usato: si riparte dalla password. */
  invalidTotpChallenge: 'dev.atlantis.sso.errors.TotpError.InvalidChallenge',
  /** Troppi tentativi per lo stesso utente (429, con `Retry-After`). */
  tooManyRequests: 'dev.atlantis.sso.errors.SecurityError.TooManyRequests',
} as const;
